import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, Globe, Clock, ScrollText, Scale } from "lucide-react";
import { api } from "@/lib/api";

function LegalShell({ icon: Icon, tag, title, children }) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-teal-600 mb-2 flex items-center gap-2"><Icon className="w-3.5 h-3.5" />{tag}</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 tracking-tight">{title}</h1>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-8 prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

export function ServiceAgreement() {
  return (
    <LegalShell icon={Shield} tag="Legal 01" title="CLEANBAZ International Platform Service Agreement">
      <p><strong>Last updated:</strong> January 2026</p>
      <h3 className="font-display font-bold text-xl text-slate-900">1. Nature of the Platform</h3>
      <p>CLEANBAZ Inc. operates <strong>solely as a digital matching broker network</strong> that connects Clients seeking cleaning services with Independent Contractors ("Cleaners"). CLEANBAZ is <strong>not the employer</strong> of any Cleaner and does not directly perform, supervise, or guarantee cleaning services.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">2. Zero Direct Employment Liability</h3>
      <p>Cleaners are Independent Contractors and are solely responsible for their own equipment, taxes, licensing, insurance, work quality, and legal compliance in their jurisdiction. CLEANBAZ holds <strong>zero direct employment liabilities</strong>, and no employer-employee relationship is created by using the platform.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">3. Payment Handling</h3>
      <p>Payments are captured at booking through Stripe. Funds are held by CLEANBAZ as the merchant of record and released to the Cleaner only after 100% service completion is confirmed. CLEANBAZ is not a bank, trust, or licensed escrow service; the term "payment-protected" refers to CLEANBAZ's internal release workflow, not a regulated escrow arrangement. CLEANBAZ retains a 20% platform commission from each transaction; the remaining 80% is routed to the Cleaner's designated Stripe Connect payout account.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">4. Governing Law</h3>
      <p>This agreement is governed by the laws of the State of Delaware, USA, without regard to conflict-of-law principles.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell icon={Globe} tag="Legal 02" title="Cross-Border Privacy Protection Protocol">
      <p><strong>Compliance:</strong> EU GDPR · California CCPA · UK Data Protection Act</p>
      <h3 className="font-display font-bold text-xl text-slate-900">1. Data We Collect</h3>
      <p>We collect only the data strictly necessary to operate the marketplace: full name, email, home address (Clients), postal service zones (Cleaners), payment details (via Stripe — never stored on CLEANBAZ servers), and government photo ID (Cleaners only, encrypted at rest).</p>
      <h3 className="font-display font-bold text-xl text-slate-900">2. GDPR Rights (EU/UK Residents)</h3>
      <p>You have the right to access, rectify, erase ("right to be forgotten"), restrict processing, port, and object to processing of your personal data. Requests are honored within 30 days at privacy@cleanbaz.com.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">3. CCPA Rights (California Residents)</h3>
      <p>California consumers may request disclosure of collected data categories, deletion of personal information, and opt-out of any sale of personal data. CLEANBAZ does not sell personal data.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">4. Home Address & Identity Safeguards</h3>
      <p>Client home addresses are shared with the assigned Cleaner only after job matching. Government photo ID documents uploaded by Cleaners are encrypted using AES-256 at rest, accessible only by the compliance verification team, and permanently purged 30 days after account termination.</p>
    </LegalShell>
  );
}

export function Cancellation() {
  return (
    <LegalShell icon={Clock} tag="Legal 03" title="24-Hour Late Cancellation Penalty Policy">
      <h3 className="font-display font-bold text-xl text-slate-900">1. Free Adjustment Window</h3>
      <p>You may reschedule or cancel any booking free of charge as long as changes are made <strong>at least 24 hours before</strong> the scheduled start time.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">2. Automated Late Mitigation Fee</h3>
      <p>If a booking is rescheduled or cancelled <strong>less than 24 hours</strong> before its start time, an automated late-mitigation processing fee of <strong>15% of the booking total</strong> will be charged against the customer's authorized card. This fee compensates the Cleaner for reserved time and lost revenue.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">3. No-Show</h3>
      <p>If the Cleaner arrives at the address and cannot access the property within a 20-minute buffer, the booking is treated as a no-show and 100% of the fee is retained.</p>
      <h3 className="font-display font-bold text-xl text-slate-900">4. Force Majeure</h3>
      <p>Late fees are waived in verified cases of medical emergency, natural disaster, or other force majeure documented within 48 hours to support@cleanbaz.com.</p>
    </LegalShell>
  );
}

// Renders the current CLEANBAZ Cleaner Agreement AND Dispute & Damage Policy
// straight from the versioned backend, so the public copy is always in sync
// with what cleaners actually accept.
function VersionedPolicyView({ endpoint, tag, icon, fallbackTitle }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(endpoint);
        setDoc(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load this policy.");
      }
    })();
  }, [endpoint]);
  const title = doc?.title || fallbackTitle;
  return (
    <LegalShell icon={icon} tag={tag} title={title}>
      {err && <p className="text-red-600 text-sm" data-testid="legal-load-error">{err}</p>}
      {!doc && !err && <p className="text-slate-500 text-sm">Loading current version…</p>}
      {doc && (
        <>
          <p className="text-xs font-mono uppercase tracking-widest text-teal-700" data-testid="legal-version">
            Version {doc.version} · Effective {doc.effective_at}
          </p>
          {doc.attorney_review_required && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              <b>Notice:</b> This document is a draft under attorney review before production use.
            </div>
          )}
          {(doc.sections || []).map((s) => (
            <div key={s.key} data-testid={`legal-section-${s.key}`}>
              <h3 className="font-display font-bold text-xl text-slate-900 mt-6">{s.title}</h3>
              <p className="whitespace-pre-wrap">{s.body}</p>
            </div>
          ))}
        </>
      )}
    </LegalShell>
  );
}

export function CleanerAgreement() {
  return (
    <VersionedPolicyView
      endpoint="/legal/cleaner-agreement/latest"
      tag="Legal 04"
      icon={ScrollText}
      fallbackTitle="CLEANBAZ Cleaner Agreement"
    />
  );
}

export function DisputePolicy() {
  return (
    <VersionedPolicyView
      endpoint="/legal/dispute-policy/latest"
      tag="Legal 05"
      icon={Scale}
      fallbackTitle="CLEANBAZ Dispute & Damage Policy"
    />
  );
}
